// Searching GitHub for a section's pull requests, and reading rows by ID.
//
// A section query of up to DASH_PAGE hits is one search that reads the rows.
// A longer one (the attention section's) searches for IDs alone, a page at a
// time, and reads the rows by ID in DASH_PAGE batches, each page's batches
// as soon as it arrives, as no one request can carry them in time. Every
// request goes through the app's scheduler (`github`), which bounds, orders
// and retries them; these functions only say what to read.
import { readLabels } from '../../api/answers';
import { GraphQLFailure, graphql, graphqlResult, isSamlError, RateLimitError } from '../../api/gh';
import { withStacks } from '../../api/nativeStacks';
import {
  DASH_IDS_PAGE,
  DASH_PAGE,
  DASH_ROWS,
  DASH_ROWS_NO_STACKS,
  DASH_SEARCH_IDS,
  DASH_SECTION,
  DASH_SECTION_NO_STACKS,
  type DashPr,
} from '../../api/queries';

/** How much of a section's matches were read: `checked` (the most recently updated hits loaded) of `total` (GitHub's match count). */
export interface SectionCut {
  checked: number;
  total: number;
}

/** A row read by ID: the row, `sso` when reading it needs SSO authorization, or null when GitHub does not show it. */
export type RowRead = DashPr | 'sso' | null;

/** A section query's hits, with GitHub's count of every match. */
export interface SearchPage {
  nodes: DashPr[];
  total: number;
  /**
   * Matches read but not loaded, after their retries: those of a later
   * page of IDs that failed, null hits (one the user may not see), pull
   * requests that moved up past the pages read and were not found on
   * re-reading the first, and those whose row failed or came back empty. The cause is the first failure; `rateLimited` says whether any
   * was a rate limit (the dashboard reloads when the pause ends).
   */
  unloaded: { count: number; cause: unknown; rateLimited: boolean } | null;
}


/** A section's read limit, when any of its queries matched more than `first`; matches that did not load were not checked. */
export function cutOf(pages: SearchPage[], first: number): SectionCut | null {
  if (!pages.some((p) => p.total > first)) return null;
  return {
    checked: pages.reduce((n, p) => n + Math.min(p.total, first) - (p.unloaded?.count ?? 0), 0),
    total: pages.reduce((n, p) => n + p.total, 0),
  };
}

/** The matches a section's queries read but could not load, with the first cause; rate-limited if any of them was. */
export function unloadedOf(pages: SearchPage[]): SearchPage['unloaded'] {
  const lost = pages.flatMap((p) => (p.unloaded ? [p.unloaded] : []));
  if (!lost.length) return null;
  return { count: lost.reduce((n, u) => n + u.count, 0), cause: lost[0].cause, rateLimited: lost.some((u) => u.rateLimited) };
}

/**
 * A row as GitHub answered it, with its native stack fields filled in where
 * the schema has none, and its labels decoded (`readLabels`: none when
 * GitHub leaves them out).
 */
function dashRow(n: DashPr): DashPr {
  return {
    ...n,
    labels: readLabels(n.labels),
    stack: n.stack ? { ...n.stack, entries: n.stack.entries ?? { nodes: [] } } : null,
    stackEntry: n.stackEntry ?? null,
  };
}

/** How a dashboard search hands over rows as they load, and is abandoned. */
export interface SearchOptions {
  /** Called with each batch of rows as it loads, before the search finishes. */
  onRows?: (rows: DashPr[]) => void;
  /**
   * Aborted when newer work supersedes the search: its waiting pages and
   * batches are dropped unsent, it reads no further page, and it rejects
   * with the signal's reason.
   */
  signal?: AbortSignal;
}

/**
 * The first `first` hits of a section query, most relevant first as the
 * query sorts them. Up to DASH_PAGE, one search reads the rows. Beyond
 * that, the rows cost GitHub more time than one request may take, so the
 * search reads IDs alone, DASH_IDS_PAGE at a time, and the rows by ID in
 * DASH_PAGE batches, each page's batches as soon as it arrives. Every
 * request is `visible` work for the scheduler (`github`), which bounds
 * them with the store's attention requests and retries transient
 * failures.
 *
 * A failure of the one search, or of the first page of IDs, rejects. What
 * a later page or batch would have loaded is counted in `unloaded` if it
 * fails. `opts.onRows` receives each batch of rows as it loads.
 */
export async function searchSection(q: string, first: number, opts: SearchOptions = {}): Promise<SearchPage> {
  const onRows = opts.onRows ?? (() => {});
  if (first <= DASH_PAGE) {
    type Res = { search: { issueCount: number; nodes: (DashPr | null)[] } };
    const res = await withStacks<Res>(DASH_SECTION, DASH_SECTION_NO_STACKS, { q, first }, { signal: opts.signal });
    const nodes = res.search.nodes.filter((n): n is DashPr => !!n?.number).map(dashRow);
    onRows(nodes);
    return { nodes, total: res.search.issueCount, unloaded: null };
  }
  const signal = opts.signal;
  type Ids = { search: { issueCount: number; pageInfo: { hasNextPage: boolean; endCursor: string | null }; nodes: ({ id?: string } | null)[] } };
  type Rows = { nodes: (DashPr | null)[] };
  const ids: string[] = [];
  const seen = new Set<string>();
  const rows = new Map<string, DashPr>();
  const reading: Promise<void>[] = [];
  let total = 0;
  let read = 0;
  let unread = 0;
  /** Hits GitHub answered as null (one the user may not see). */
  let hidden = 0;
  /** IDs a later page repeated: as many pull requests moved up past the pages already read. */
  let shifted = 0;
  const causes: unknown[] = [];
  const readRows = async (batch: string[]) => {
    try {
      const res = await withStacks<Rows>(DASH_ROWS, DASH_ROWS_NO_STACKS, { ids: batch }, { signal });
      const loaded = res.nodes.filter((n): n is DashPr => !!n?.number).map(dashRow);
      for (const n of loaded) rows.set(n.id, n);
      onRows(loaded);
    } catch (e) {
      causes.push(e);
    }
  };
  const idPage = (after: string | null, count = Math.min(DASH_IDS_PAGE, first - read)) =>
    graphql<Ids>(DASH_SEARCH_IDS, { q, first: count, after }, { signal });
  /** Takes a page's new IDs and reads their rows; returns how many were new. */
  const take = (page: Ids, front = false) => {
    const found = page.search.nodes.flatMap((n) => (n?.id ? [n.id] : []));
    const fresh = found.filter((id) => !seen.has(id));
    for (const id of fresh) seen.add(id);
    if (front) ids.unshift(...fresh);
    else ids.push(...fresh);
    for (let i = 0; i < fresh.length; i += DASH_PAGE) reading.push(readRows(fresh.slice(i, i + DASH_PAGE)));
    return { found, fresh };
  };
  for (let after: string | null = null; ; ) {
    let page: Ids;
    try {
      page = await idPage(after);
    } catch (e) {
      if (after === null) throw e;
      unread = Math.min(total, first) - read;
      causes.push(e);
      break;
    }
    total = page.search.issueCount;
    read += page.search.nodes.length;
    const { found, fresh } = take(page);
    hidden += page.search.nodes.length - found.length;
    shifted += found.length - fresh.length;
    const next = page.search.pageInfo;
    if (!next.hasNextPage || !next.endCursor || read >= first || signal?.aborted) break;
    after = next.endCursor;
  }
  // GitHub's cursors are offsets: a pull request updated while the pages
  // were read moved to the top, past them, and the next page repeated what
  // it pushed down. Read the top again for it.
  if (shifted && !signal?.aborted)
    try {
      const top = await idPage(null, Math.min(DASH_IDS_PAGE, first));
      shifted = Math.max(0, shifted - take(top, true).fresh.length);
    } catch (e) {
      causes.push(e);
    }
  await Promise.all(reading);
  signal?.throwIfAborted();
  const nodes = ids.flatMap((id) => rows.get(id) ?? []);
  const count = unread + hidden + shifted + ids.length - nodes.length;
  return {
    nodes,
    total,
    unloaded: count
      ? {
          count,
          cause: causes[0] ?? new Error('GitHub returned no data for them'),
          rateLimited: causes.some((e) => e instanceof RateLimitError),
        }
      : null,
  };
}

/**
 * Dashboard rows by node ID (at most DASH_PAGE), in order, with null for an
 * ID GitHub does not show the viewer; transient failures are retried (by
 * the scheduler). The
 * app's `DashboardCapabilities.rows`.
 */
export async function readRows(ids: string[]): Promise<RowRead[]> {
  type Rows = { nodes: (DashPr | null)[] | null };
  // Stack members are named only where the schema has native stacks, so DASH_ROWS needs no fallback.
  // Fatal errors fail the request, which the scheduler retries while they may pass.
  const { data, errors } = await graphqlResult<Rows>(DASH_ROWS, { ids });
  if (!data.nodes) throw new GraphQLFailure(errors, data);
  const sso = new Set(errors.filter(isSamlError).map((e) => e.path?.[1]));
  return ids.map((_, i) => {
    const n = data.nodes?.[i];
    if (n?.number) return dashRow(n);
    return sso.has(i) ? 'sso' : null;
  });
}

/** Union of a section's query results, newest update first. */
export function mergeHits(lists: DashPr[][]): DashPr[] {
  const seen = new Set<string>();
  const out: DashPr[] = [];
  for (const list of lists)
    for (const p of list) {
      if (!p || !p.number || seen.has(p.id)) continue;
      seen.add(p.id);
      out.push(p);
    }
  return out.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
}
